import { Fragment, useState } from 'react'
import { Box, Divider, Link, Button, Typography } from '@vp/swan'
import type { AbsoluteGuarantee } from '../../../types/site-footer'
import { ComponentType } from '../../../types/components-map'
import { DEFAULT_LOCALE, DEFAULT_TENANT, NATIONAL_PEN_TENANT } from '../../../constants/config'
import PrintYourPossibleLogo from './PrintYourPossibleLogo'
import { AbsoluteGuaranteeModal } from './AbsoluteGuaranteeModal'
import { useI18n } from '../../../context/TranslationContext'
import { MountIds } from '../../../constants/hydration'

interface AbsoluteGuaranteeSectionProps {
  absoluteGuarantee: AbsoluteGuarantee
  locale?: string
  tenant?: string
}

type Segment = { type: 'text'; content: string } | { type: 'bold'; content: string } | { type: 'link'; content: string; href: string }

/**
 * Parses an inline HTML string into an ordered list of text, bold, and link segments.
 * Uses regex-based parsing to work in both browser and server (SSR) environments.
 */
function parseInlineHtml(html: string): Segment[] {
  const segments: Segment[] = []
  // Match <a>, <strong>, or <b> tags
  const inlineRegex = /<a[^>]+href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>|<(strong|b)>(.*?)<\/\3>/gi
  let lastIndex = 0
  let match

  while ((match = inlineRegex.exec(html)) !== null) {
    if (match.index > lastIndex) {
      const text = html.slice(lastIndex, match.index).replace(/<[^>]+>/g, '')
      if (text) segments.push({ type: 'text', content: text })
    }

    if (match[1] !== undefined) {
      // <a> match
      segments.push({
        type: 'link',
        content: match[2]?.replace(/<[^>]+>/g, '') ?? '',
        href: match[1],
      })
    } else {
      // <strong> or <b> match
      segments.push({
        type: 'bold',
        content: (match[4] ?? '').replace(/<[^>]+>/g, ''),
      })
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < html.length) {
    const text = html.slice(lastIndex).replace(/<[^>]+>/g, '')
    if (text) segments.push({ type: 'text', content: text })
  }

  return segments
}

/**
 * Splits HTML into paragraphs and parses each paragraph's inline content.
 * Falls back to treating the whole string as a single paragraph.
 * Returns an empty array if html is null or empty.
 */
function parseHtmlToParagraphs(html: string | null | undefined): Segment[][] {
  if (!html) return []

  const paragraphs = html.match(/<p[^>]*>([\s\S]*?)<\/p>/gi)

  if (!paragraphs) {
    return [parseInlineHtml(html)]
  }

  return paragraphs.map((pHtml) => {
    const innerMatch = pHtml.match(/<p[^>]*>([\s\S]*?)<\/p>/i)
    return parseInlineHtml(innerMatch?.[1] ?? pHtml ?? '')
  })
}

export const AbsoluteGuaranteeSection: React.FC<AbsoluteGuaranteeSectionProps> = ({ absoluteGuarantee, locale, tenant }) => {
  const { html, analyticsId } = absoluteGuarantee
  const showPYPLogo = locale?.toLowerCase() === DEFAULT_LOCALE.toLowerCase() && tenant === DEFAULT_TENANT
  const showNPLogo = tenant === NATIONAL_PEN_TENANT

  const trackingData = JSON.stringify({
    bookendName: ComponentType.FOOTER,
    navigationDetailPath: analyticsId ? [analyticsId] : [],
  })

  if (!showNPLogo && !showPYPLogo) {
    return (
      <Box marginBottom="7">
        <Typography dangerouslySetInnerHTML={{ __html: html ?? '' }} data-tracking={trackingData} />
      </Box>
    )
  }

  const paragraphs = parseHtmlToParagraphs(html)

  return (
    <>
      {showPYPLogo && <PrintYourPossibleLogo />}
      {showNPLogo && (
        <Box id={MountIds.NP_ABSOLUTE_GUARANTEE}>
          <NPAbsoluteGuaranteeLogo absoluteGuarantee={absoluteGuarantee} />
        </Box>
      )}
      <Divider my="5" />
      {paragraphs.map((segments, pIndex) => (
        <Typography key={pIndex} fontSkin="body-standard" mb={pIndex === paragraphs.length - 1 ? '7' : '4'}>
          {segments.map((section, sIndex) => (
            <Sections key={sIndex} segment={section} trackingData={trackingData} />
          ))}
        </Typography>
      ))}
    </>
  )
}

export const NPAbsoluteGuaranteeLogo = ({ absoluteGuarantee }: { absoluteGuarantee: AbsoluteGuarantee }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { translate } = useI18n()
  const { sectionImageContents, sectionImageAltText = translate('viewGuarantees'), modal } = absoluteGuarantee

  return (
    <>
      <Button onClick={() => setIsModalOpen(true)} skin="unstyled" aria-label={sectionImageAltText}>
        {sectionImageContents ? <Box dangerouslySetInnerHTML={{ __html: sectionImageContents }} display="flex" /> : sectionImageAltText}
      </Button>
      {modal && <AbsoluteGuaranteeModal modal={modal} isOpen={isModalOpen} onDismiss={() => setIsModalOpen(false)} />}
    </>
  )
}

const Sections = ({ segment, trackingData }: { segment: Segment; trackingData: string }) => {
  if (segment.type === 'link') {
    return (
      <Link href={segment.href} data-tracking={trackingData}>
        {segment.content}
      </Link>
    )
  }

  if (segment.type === 'bold') {
    return (
      <Typography as="span" fontSkin="body-standard-bold">
        {segment.content}
      </Typography>
    )
  }

  return <Fragment>{segment.content}</Fragment>
}
